<template>
  <form class="mcc" data-clarity-mask="true" @submit.prevent>
    <h3>What missed calls could cost you</h3>
    <p class="mcc-intro">Type your own numbers from your call log and past jobs. Nothing is filled in for you, and nothing you type leaves this page.</p>
    <div class="mcc-fields">
      <label>
        <span>Missed calls a week</span>
        <input v-model.number="calls" type="number" inputmode="numeric" min="0" step="1" />
      </label>
      <label>
        <span>Callers who would have booked (%)</span>
        <input v-model.number="share" type="number" inputmode="decimal" min="0" max="100" step="1" />
      </label>
      <label>
        <span>Average job value (USD)</span>
        <input v-model.number="value" type="number" inputmode="decimal" min="0" step="1" />
      </label>
    </div>
    <div class="mcc-result" aria-live="polite">
      <span>Revenue at risk each month</span>
      <strong translate="no">{{ monthly === null ? "—" : formatted }}</strong>
    </div>
    <p class="mcc-note">Missed calls a week × 52 ÷ 12 × the share who would have booked × the job value. It's a rough figure for work you might lose, not a forecast of what a text will win back.</p>
  </form>
</template>

<script setup>
const calls = ref("");
const share = ref("");
const value = ref("");

const valid = (n, max = Infinity) => typeof n === "number" && Number.isFinite(n) && n >= 0 && n <= max;

const monthly = computed(() => {
  if (!valid(calls.value) || !valid(share.value, 100) || !valid(value.value)) return null;
  return ((calls.value * 52) / 12) * (share.value / 100) * value.value;
});

const formatted = computed(() =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(monthly.value),
);
</script>

<style scoped>
.mcc {
  margin-top: 2.5rem;
  padding: 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.6rem;
  background: #f8fafc;
}

.mcc h3 {
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 800;
}

.mcc-intro,
.mcc-note {
  max-width: 48rem;
  margin-top: 0.4rem;
  color: #475569;
  font-size: 0.92rem;
  line-height: 1.55;
}

.mcc-fields {
  display: grid;
  gap: 1rem;
  margin-top: 1.25rem;
}

.mcc-fields label {
  display: grid;
  gap: 0.35rem;
  color: #0f172a;
  font-size: 0.88rem;
  font-weight: 700;
}

.mcc-fields input {
  width: 100%;
  min-height: 2.75rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  background: #fff;
  color: #0f172a;
  font-size: 1rem;
  font-variant-numeric: tabular-nums;
}

.mcc-fields input:focus-visible {
  outline: 2px solid #0e7490;
  outline-offset: 1px;
}

.mcc-result {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.5rem 1rem;
  margin-top: 1.25rem;
  padding-top: 1rem;
  border-top: 1px solid #e2e8f0;
  color: #334155;
  font-weight: 700;
}

.mcc-result strong {
  color: #0f172a;
  font-size: 1.6rem;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.mcc-note {
  font-size: 0.82rem;
  color: #64748b;
}

@media (min-width: 768px) {
  .mcc-fields {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.dark .mcc {
  border-color: #243247;
  background: #0f172a;
}

.dark .mcc h3,
.dark .mcc-fields label,
.dark .mcc-result strong {
  color: #f8fafc;
}

.dark .mcc-intro,
.dark .mcc-result {
  color: #cbd5e1;
}

.dark .mcc-note {
  color: #94a3b8;
}

.dark .mcc-fields input {
  border-color: #334155;
  background: #020617;
  color: #f8fafc;
}

.dark .mcc-result {
  border-color: #243247;
}
</style>
